"use client"

import { CalendarCheck } from "lucide-react"
import { cn } from "@/lib/utils"
import { regionDisplayName } from "@/lib/region-display"
import {
  getMonthlyTasks,
  getCurrentMonthIndex,
  getMonthName,
  normalizeGrassKey,
} from "@/data/guide-monthly-tasks"

const PRIORITY_DOT: Record<'high' | 'medium' | 'low', string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-gray-400',
}

/**
 * Shared month/task split for the result plan surfaces: the lead task (first
 * high-priority, else first) vs the rest of the month. Exported so the digest
 * can name the rest count in its ask lead-in ("3 more July tasks below") and
 * render the rest block separately below the ask.
 */
export function getResultPlanMonth(grassType: string) {
  const key = normalizeGrassKey(grassType) ?? grassType
  const monthIndex = getCurrentMonthIndex()
  const tasks = getMonthlyTasks(key, monthIndex)
  const leadIndex = tasks.findIndex((t) => t.priority === 'high')
  const leadTaskIndex = leadIndex === -1 ? (tasks.length > 0 ? 0 : -1) : leadIndex
  return {
    monthName: getMonthName(monthIndex),
    leadTask: leadTaskIndex === -1 ? undefined : tasks[leadTaskIndex],
    restTasks: tasks.filter((_, i) => i !== leadTaskIndex),
  }
}

/** The "Also this month" task list, standalone — rendered by the digest below
 * the ask (resequenced 2026-07-06 so the ask lands before the free giveaway). */
export function ResultPlanRestTasks({ grassType, className }: { grassType: string; className?: string }) {
  const { restTasks } = getResultPlanMonth(grassType)
  if (restTasks.length === 0) return null
  return (
    <div className={cn("rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3", className)}>
      <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-500">
        Also this month
      </div>
      <ul className="space-y-1.5">
        {restTasks.map((t, i) => (
          <li key={i} className="flex items-start gap-2 min-w-0">
            <span
              className={cn(
                "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                PRIORITY_DOT[t.priority],
              )}
              aria-hidden="true"
            />
            <span className="flex-1 min-w-0 text-[13.5px] text-gray-900 leading-snug">
              {t.task}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface ResultPlanCardProps {
  grassType: string
  region?: string
  className?: string
  /**
   * Overrides the default "Built for {grass}" header. Used by the moderate
   * (50-69%) confidence band to frame the plan as a regional best guess
   * rather than a confirmed visual match.
   */
  headerOverride?: string
  /**
   * Hide the "+11 more months" footer link. The email-first arm's locked-plan
   * unit already teases the remaining months, so the link would be redundant.
   * Defaults true (shown).
   */
  showMoreMonthsLink?: boolean
  /**
   * Render without the outer card chrome (border, padding, background, shadow).
   * Used when nested inside `ResultCarePlanDigest`, which owns the shared card.
   */
  bare?: boolean
  /**
   * Skip the "Also this month" rest-of-month block. The digest renders it
   * separately (via `ResultPlanRestTasks`) BELOW the save ask so the ask lands
   * before the free giveaway. Defaults false (full card).
   */
  hideRestTasks?: boolean
  /**
   * Skip the "Your free care plan" kicker and "Built for {grass}" line.
   * `bare` only strips the card chrome, so a host that already titles the
   * plan (the digest says "Your free plan is ready") would otherwise stack
   * two free-plan headings. Defaults false (header shown).
   */
  hideHeader?: boolean
}

export function ResultPlanCard({ grassType, region, className, headerOverride, showMoreMonthsLink = true, bare = false, hideRestTasks = false, hideHeader = false }: ResultPlanCardProps) {
  const { monthName, leadTask, restTasks } = getResultPlanMonth(grassType)

  // Season-suffix stripped ("Northeast", not "Northeast - Cool Season") — the
  // result hero already states the season.
  const displayRegion = regionDisplayName(region)
  const headerLine = headerOverride
    ? headerOverride
    : displayRegion
      ? `Built for ${grassType} in the ${displayRegion}.`
      : `Built for ${grassType}.`

  return (
    <div
      id={bare ? undefined : "care-plan-preview"}
      className={cn(
        "relative w-full max-w-full min-w-0 overflow-hidden",
        bare
          ? ""
          : "rounded-2xl border border-gray-200 bg-white p-4 md:p-7 shadow-sm",
        className,
      )}
    >
      {!hideHeader && (
        <div className="mb-4">
          <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.06em] text-green-700 mb-1.5">
            Your free care plan
          </div>
          <div className="text-lg md:text-xl font-extrabold tracking-tight text-gray-900 leading-tight">
            {headerLine}
          </div>
        </div>
      )}

      {leadTask ? (
        <>
          <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
            <span
              className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-green-600 text-white"
              aria-hidden="true"
            >
              <CalendarCheck className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <div className="min-w-0">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-green-700">
                Start here · this {monthName}
              </div>
              <div className="mt-0.5 text-[15px] font-bold leading-snug text-gray-900">
                {leadTask.task}
              </div>
            </div>
          </div>

          {!hideRestTasks && restTasks.length > 0 && (
            <div className="mt-2.5 rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3">
              <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-500">
                Also this month
              </div>
              <ul className="space-y-1.5">
                {restTasks.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 min-w-0">
                    <span
                      className={cn(
                        "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                        PRIORITY_DOT[t.priority],
                      )}
                      aria-hidden="true"
                    />
                    <span className="flex-1 min-w-0 text-[13.5px] text-gray-900 leading-snug">
                      {t.task}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-xl border border-green-200 bg-green-50/60 px-4 py-3">
          <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-green-700">
            This month · {monthName}
          </div>
          <p className="text-[13px] text-gray-600 leading-snug">
            Your seasonal plan is below. Scroll down for products, tools, and your full 12-month timeline.
          </p>
        </div>
      )}

      {showMoreMonthsLink && (
        <div className="mt-3 text-center">
          <a
            href="#care-plan-upgrade"
            className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-green-700 hover:text-green-800 transition-colors"
          >
            + 11 more months in your full plan
            <span aria-hidden="true">→</span>
          </a>
        </div>
      )}
    </div>
  )
}
